import type { OrganizationInvitationResponse } from "@kontextwerk/control-web-client"
import { userDisplayName } from "@kontextwerk/control-web-frame/shared/logic/user-presentation"
import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import type { ColumnDef, DataRow } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
import { mdiAccountCheckOutline, mdiAccountLockOutline, mdiEmailFastOutline } from "@mdi/js"
import type { KehrblattFrameMember } from "../../../../../../../../../../../../../api/kehrblatt/organization-frame"
import {
  findRole,
  roleLabel,
  type RoleKind,
  type RoleReference,
} from "../../../../../../../_shared/_logic/role-presentation"

export type UserAccountStatusKey = "active" | "suspended" | "invited"

export const USER_ACCOUNT_STATUS_DEFINITIONS: Record<
  UserAccountStatusKey,
  StatusDefinition<UserAccountStatusKey>
> = {
  active: {
    value: "active",
    label: "Aktiv",
    color: "emerald",
    icon: mdiAccountCheckOutline,
    description: "Der Benutzer hat Zugriff auf diese Organisation.",
  },
  suspended: {
    value: "suspended",
    label: "Gesperrt",
    color: "neutral",
    icon: mdiAccountLockOutline,
    description: "Der Zugriff ist gesperrt, bis der Benutzer wieder entsperrt wird.",
  },
  invited: {
    value: "invited",
    label: "Eingeladen",
    color: "blue",
    icon: mdiEmailFastOutline,
    description: "Die Einladung wurde versendet und noch nicht angenommen.",
  },
}

export type UserProfileCell = {
  displayName: string | null
  email: string | null
  avatarUrl: string | null
}

type UserRowBase = DataRow & {
  id: string
  profile: UserProfileCell
  name: string
  email: string
  roleId: string | null
  roleKind: RoleKind | null
  roleLabel: string
  statusKey: UserAccountStatusKey | null
  statusLabel: string
}

/** Roster member; membership facts are null where the confirmed context may not read them. */
export type MemberRow = UserRowBase & {
  kind: "member"
  userId: string
  membershipId: string
  membershipRevision: number | null
  isSelf: boolean
}

export type InvitationRow = UserRowBase & {
  kind: "invitation"
  invitationId: string
}

export type UserRow = MemberRow | InvitationRow

const MISSING = "—"
const STATUS_ORDER: Record<UserAccountStatusKey, number> = { active: 2, invited: 1, suspended: 0 }
const collator = new Intl.Collator("de", { sensitivity: "base", numeric: true })

const text = (value: unknown): string => (typeof value === "string" ? value.trim() : "")

const statusOf = (member: KehrblattFrameMember): UserAccountStatusKey | null =>
  member.membership_status === "active" || member.membership_status === "suspended"
    ? member.membership_status
    : null

const roleFacts = (role: RoleReference | null) => ({
  roleId: role?.id ?? null,
  roleKind: role?.kind ?? null,
  roleLabel: roleLabel(role) ?? MISSING,
})

export const buildMemberRows = ({
  members,
  roles,
  currentMembershipId,
  avatarUrl,
}: {
  members: readonly KehrblattFrameMember[]
  roles: ReadonlyMap<string, RoleReference>
  currentMembershipId: string | null
  avatarUrl: (value: string | null | undefined) => string | null
}): MemberRow[] =>
  members
    .map((member): MemberRow => {
      const email = text(member.email)
      const name = userDisplayName({ ...member, email }) || MISSING
      const statusKey = statusOf(member)
      const role = findRole(roles, member.role_id)
      const revision = member.membership_revision

      return {
        id: `member:${member.membership_id}`,
        kind: "member",
        userId: member.id,
        membershipId: member.membership_id,
        membershipRevision: typeof revision === "number" && revision >= 1 ? revision : null,
        isSelf: Boolean(
          currentMembershipId &&
          member.membership_id.toLowerCase() === currentMembershipId.toLowerCase(),
        ),
        profile: {
          displayName: name === MISSING ? null : name,
          email: email || null,
          avatarUrl: avatarUrl(member.profile_image_url),
        },
        name,
        email,
        ...roleFacts(role),
        // An unreadable role id stays usable for status changes, it is just not labelled.
        roleId: role?.id ?? (text(member.role_id) || null),
        statusKey,
        statusLabel: statusKey ? USER_ACCOUNT_STATUS_DEFINITIONS[statusKey].label : MISSING,
      }
    })
    .sort((left, right) => collator.compare(left.name, right.name))

export const buildInvitationRows = ({
  invitations,
  roles,
}: {
  invitations: readonly OrganizationInvitationResponse[]
  roles: ReadonlyMap<string, RoleReference>
}): InvitationRow[] =>
  invitations
    .map((invitation): InvitationRow => ({
      id: `invitation:${invitation.id}`,
      kind: "invitation",
      invitationId: invitation.id,
      profile: {
        displayName: invitation.recipient_email,
        email: invitation.recipient_email,
        avatarUrl: null,
      },
      name: invitation.recipient_email,
      email: invitation.recipient_email,
      ...roleFacts(findRole(roles, invitation.role_id)),
      statusKey: "invited",
      statusLabel: USER_ACCOUNT_STATUS_DEFINITIONS.invited.label,
    }))
    .sort((left, right) => collator.compare(left.email, right.email))

/** Members first, then pending invitations; both in a stable alphabetical order. */
export const buildUserRows = (input: {
  members: readonly KehrblattFrameMember[]
  invitations: readonly OrganizationInvitationResponse[]
  roles: ReadonlyMap<string, RoleReference>
  currentMembershipId: string | null
  avatarUrl: (value: string | null | undefined) => string | null
}): UserRow[] => [...buildMemberRows(input), ...buildInvitationRows(input)]

export const filterUserRows = (rows: readonly UserRow[], query: string): UserRow[] => {
  const needle = query.trim().toLowerCase()

  if (!needle) return [...rows]

  return rows.filter(
    (row) => row.name.toLowerCase().includes(needle) || row.email.toLowerCase().includes(needle),
  )
}

/** Signature of every rendered fact; a change refreshes the client-side table. */
export const userRowsSignature = (rows: readonly UserRow[]): string =>
  JSON.stringify(
    rows.map((row) => [
      row.id,
      row.name,
      row.email,
      row.roleLabel,
      row.statusKey,
      row.profile.avatarUrl,
      row.kind === "member" ? row.membershipRevision : null,
    ]),
  )

export const buildUserColumns = (showEmail: boolean): ColumnDef[] => [
  { id: "profile", label: "" },
  {
    id: "name",
    label: "Name",
    isPrimary: true,
    sortable: true,
    sortKey: "name",
    sortType: "text",
  },
  ...(showEmail
    ? [
        {
          id: "email",
          label: "E-Mail",
          sortable: true,
          sortKey: "email",
          sortType: "text" as const,
        },
      ]
    : []),
  {
    id: "roleLabel",
    label: "Rolle",
    sortable: true,
    sortKey: "role",
    sortType: "text",
  },
  {
    id: "statusLabel",
    label: "Status",
    sortable: true,
    sortKey: "status",
    sortType: "number",
    defaultSortDir: "desc",
    getSortValue: (row) => {
      const key = (row as UserRow).statusKey

      return key ? STATUS_ORDER[key] : null
    },
  },
]
